import { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Library, AlertTriangle, Languages } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  // "next" query param — used by the public /scan/:item page (and any other
  // deep-link) to send the visitor back to where they came from after a
  // successful log-in. Restricted to same-origin absolute paths for safety.
  const nextParam = new URLSearchParams(location.search).get("next") || "";
  const safeNext = nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    const fallback = user.role === "customer" ? "/quotes" : user.role === "user_admin" ? "/users" : "/";
    return <Navigate to={safeNext || fallback} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate(safeNext || "/");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm bg-white border border-border rounded-md p-8 animate-fade-in-up relative">
        {/* 2026-02-26 · Compact zh/en toggle in the top-right corner. */}
        <button
          type="button"
          onClick={toggleLang}
          data-testid="login-lang-toggle"
          className="absolute top-3 right-3 flex items-center gap-1 h-7 px-2 rounded-md border border-border text-[11px] font-mono text-muted-foreground hover:text-foreground hover:border-brand transition-colors"
          title={t("login.toggleLanguage")}
        >
          <Languages size={12} />
          {lang === "zh" ? "EN" : "中"}
        </button>
        <div className="flex flex-col items-center mb-7">
          <div className="w-11 h-11 rounded-md border border-border flex items-center justify-center mb-3">
            <Library size={18} className="text-brand" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground">{t("app.title")}</h1>
          <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("app.caption")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          <div className="space-y-1.5">
            <Label htmlFor="email">{t("login.email")}</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              placeholder="admin@quotesys.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">{t("login.password")}</Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="login-error-message">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-brand hover:bg-brand-hover rounded-md h-10 font-semibold transition-colors"
          >
            {loading ? "..." : t("login.submit")}
          </Button>
        </form>
      </div>
    </div>
  );
}
